import { Injectable } from '@angular/core';
import { HelperService } from './helper.service';
import { NetwordDetectionService } from 'modules/app/services/network-detection.service';
import { DesktopDatabaseHelperService } from './desktop-database-helper.service';

@Injectable()
export class EbookHelperService {

    /**
     * array of {
     * ebookCode
     * data : {
     *      manifest object fetched from server
     *  }
     * }
     */
    manifestList = [];
    oldManifestList = [];

    constructor(
        private helperService: HelperService,
        private networkService: NetwordDetectionService,
        private desktopDatabaseHelperService: DesktopDatabaseHelperService,
    ) {
    }

    /**
     * function to purge previous DB and add data fetched from server.
     * @param data data as returned from server in fetch call.
     */
    async addAllManifestsToDB(data, keepPreviousData = false) {
        let modeledData = [];
        if (data) {
            Object.keys(data).forEach(key => {
                modeledData.push({ 'ebookCode': key, 'data': data[key] })
            });

            this.manifestList = this.manifestList.concat(modeledData);

            await this.desktopDatabaseHelperService.addAllManifestsToDB(modeledData, keepPreviousData);
            this.getAllManifestsFromDB();
        }
    }

    /**
     * Fetch manifests from DB and return
     * @returns List of {book code : manifest data}
     */
    async getAllManifestsFromDB() {
        this.manifestList = await this.desktopDatabaseHelperService.getAllManifestsFromDB();
        return this.manifestList;
    }

    /**
     * Fetch old manifests from DB before fresh manifests are fetched from server and return
     */
    public async getOldManifestsFromDB() {
        this.oldManifestList = await this.desktopDatabaseHelperService.getOldManifestsFromDB();
        return this.oldManifestList;
    }


    /**
     *
     * function to fetch manifests from the bookCode - bundleCode object
     * If online and first launch, fetch from Server, purge Db and insert to db , return fetched
     * If online and not first launch, fetch from Server that are not present in DB, update fetch in DB
     * and return fetched + already present. If the entire product is already present, do not fetch.
     * If offline, return fetch from DB.
     * @param options - array or { bookCode, bundleCode }
     * @returns {bookcode : manifest Object}
     */
    public fetchManifests(options) {
        // if online, fetch from server, 
        // if offline, fetch from DB
        // if no data in fetch from  updatedoption, do not fetch

        // filter data already present and not present.

        let manifestMap = {};
        let updatedOptions = options;
        let finalData = {};

        // if manifestList is present, create a map.
        if (this.manifestList.length > 0) {
            this.manifestList.forEach(book => {
                manifestMap[book.ebookCode] = book.data
            })
        }

        // filter out books whose manifest is already present.
        // add the manifest in final data object.
        updatedOptions.books = updatedOptions.books.filter(book => {
            let manifestData = manifestMap[book.bookCode];
            if (manifestData) {
                finalData[book.bookCode] = manifestData;
                return false
            }
            return true;
        });


        return new Promise(async (resolve, reject) => {

            if (await this.networkService.isOnline(true)) {

                try {
                    if (updatedOptions.books.length) {
                        let data = await this.helperService.getManifests(updatedOptions);
                        if (this.manifestList.length == 0) {
                            this.addAllManifestsToDB(data, false);
                            return resolve(data);
                        } else {
                            this.addAllManifestsToDB(data, true);
                            Object.keys(data).forEach(key => {
                                finalData[key] = data[key];
                            })
                            return resolve(finalData);
                        }

                    } else {
                        return resolve(finalData);
                    }
                } catch (error) {
                    reject(error);
                }
            } else {
                await this.getAllManifestsFromDB();
                // if manifestList is present, create a map.
                if (this.manifestList.length > 0) {
                    this.manifestList.forEach(book => {
                        manifestMap[book.ebookCode] = book.data
                    })
                }
                updatedOptions.books = updatedOptions.books.filter(book => {
                    let manifestData = manifestMap[book.bookCode];
                    if (manifestData) {
                        finalData[book.bookCode] = manifestData;
                        return false
                    }
                    return true;
                });

                return resolve(finalData);
            }

        });

    }

    /**
     * reset the local manifest variable
     */
    public resetLocalCache() {
        this.manifestList = [];
    }

    public getSingleManifest(productCode) {
        return this.manifestList.find(manifest => manifest.ebookCode == productCode);
    }

    public calculateManifestSize(manifest, loManifests) {

        let fileSizeUnit = "B";
        let fileSize = manifest.shared.fileSize.zip ? manifest.shared.fileSize.zip : (0 || manifest.shared.fileSize);
        let version = manifest.shared.productVersion;
        let unZippedFileSize = manifest.shared.fileSize.unzip ? manifest.shared.fileSize.unzip : fileSize;
        let notesDrawingSpaceBuffer = 10485760;

        if (manifest.items) {
            manifest.items.forEach((item, index) => {
                let itemSize = item.fileSize.zip ? item.fileSize.zip : item.fileSize;
                fileSize += itemSize;
                if (index != manifest.length - 1) {
                    //adding max of unzipped size and zipped file  
                    unZippedFileSize += item.fileSize.unzip ? Math.max(item.fileSize.unzip, itemSize) : itemSize;
                } else {
                    unZippedFileSize += item.fileSize.unzip ? (item.fileSize.unzip + itemSize) : itemSize;
                }
            });
        }

        //if lo available
        if (loManifests) {
            for (let loManifest in loManifests) {
                if (loManifests[loManifest].shared) {
                    fileSize += loManifests[loManifest].shared.fileSize.zip;
                    unZippedFileSize += loManifests[loManifest].shared.fileSize.unzip;
                }
                if (loManifests[loManifest].items) {
                    loManifests[loManifest].items.forEach((item, index) => {
                        fileSize += item.fileSize.zip;
                        unZippedFileSize += item.fileSize.unzip;
                    });
                }
            }
        }

        unZippedFileSize += notesDrawingSpaceBuffer;
        let unZippedFile = this.convertBytes(unZippedFileSize);
        unZippedFileSize = unZippedFile["size"];
        let unZippedFileSizeUnit = unZippedFile["unit"];

        if (fileSize >= 1073741824) {
            fileSizeUnit = "GB";
            fileSize = Math.round((fileSize / 1073741824) * 100) / 100;
        } else if (fileSize >= 1048576) {
            fileSizeUnit = "MB";
            fileSize = Math.round((fileSize / 1048576) * 100) / 100;
        } else if (fileSize >= 1024) {
            fileSizeUnit = "KB";
            fileSize = Math.round((fileSize / 1024) * 100) / 100;
        } else {
            fileSize = Math.round((fileSize) * 100) / 100;
        }

        return {
            fileSize, version, fileSizeUnit, unZippedFileSize, unZippedFileSizeUnit
        }
    }

    //Convert bytes to data units
    convertBytes(bytes, decimals = 2) {
        if (bytes == 0) return 0;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return {
            size: parseFloat((bytes / Math.pow(1024, i)).toFixed(decimals)),
            unit: sizes[i]
        }
    }

    public getManifestSize(productCode) {
        let manifestData = this.getSingleManifest(productCode);
        let originalManifest = manifestData && JSON.parse(JSON.stringify(manifestData));
        let response;

        if (originalManifest && originalManifest.data) {
            response = this.calculateManifestSize(originalManifest.data.manifest, originalManifest.data.loManifests);
        }

        return { ...response };

    }

    public async getBookMeta(bookCode) {

        // check for manifests in current list.
        // check for manifests in old DB list.
        // check and wait for manifest in current list.
        // if found at any place, return data,
        // else return undefined.

        let manifestFound;
        if (this.manifestList.length) {
            console.log('in new');
            manifestFound = this.manifestList.find(manifest => manifest.ebookCode == bookCode);
        }
        if (!manifestFound && this.oldManifestList.length) {
            console.log('in old');
            manifestFound = this.oldManifestList.find(manifest => manifest.ebookCode == bookCode);
        }

        if (!manifestFound) {
            manifestFound = await new Promise(async (resolve, reject) => {
                let timeout = 1000;
                let iterations = 3;
                let bookManifest;

                for (let i = 0; i < iterations; i++) {
                    console.log(i);
                    await new Promise((res) => { setTimeout(() => { res(true) }, timeout); });
                    if (this.manifestList.length) {
                        bookManifest = this.manifestList.find(manifest => manifest.ebookCode == bookCode);
                    }
                    if (bookManifest) {
                        console.log('found in retry');
                        resolve(bookManifest);
                        break;
                    }
                }

                if (!bookManifest) {
                    resolve(undefined);
                }

            })
        }

        return manifestFound && manifestFound.data && manifestFound.data.book && manifestFound.data.book.meta;
    }

    public getPEMetaFromLinkedBook(peCode, bookCode, linkedDownloadedBookCode) {

        // assuming there will atleast be a successful manifest call either new or old and we do not need to wait for it to complete.
        // find linked book in current manifest.
        // find linked downloaded book in current manifest
        // find linked book in old manifest
        // find linked downloaded book in old manifest

        let bookManifest = this.manifestList.find(manifest => manifest.ebookCode == bookCode);

        if (!bookManifest || !bookManifest.data) {
            bookManifest = this.manifestList.find(manifest => manifest.ebookCode == linkedDownloadedBookCode);
        }
        if (!bookManifest || !bookManifest.data) {
            bookManifest = this.oldManifestList.find(manifest => manifest.ebookCode == bookCode);
        }
        if (!bookManifest || !bookManifest.data) {
            bookManifest = this.oldManifestList.find(manifest => manifest.ebookCode == linkedDownloadedBookCode);
        }
        let peManifest = bookManifest && bookManifest.data && bookManifest.data.loPEDAta && bookManifest.data.loPEDAta[peCode];
        return peManifest.meta;
    }

}
